import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminReviews() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/endorsements/pending");
      setList(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/endorsements/pending/${id}/approve`);
      toast.success("Endorsement approved & published");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Discard this review?")) return;
    try {
      await api.delete(`/admin/endorsements/pending/${id}`);
      toast.success("Review discarded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Discard failed");
    }
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-reviews-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-reviews-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="border-b border-rule pb-6 mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
            Submitted Reviews
          </p>
          <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
            Pending reviews
            <span className="ml-3 text-2xl text-ink-soft" data-testid="admin-reviews-count">
              ({list.length})
            </span>
          </h1>
          <p className="mt-3 text-sm text-ink-soft">
            Readers submit reviews from the bottom of <Link to="/book" className="text-terracotta link-underline">/book</Link>. Approve to publish on the public page.
          </p>
        </div>

        {loading ? (
          <p className="text-ink-soft" data-testid="admin-reviews-loading">Loading…</p>
        ) : list.length === 0 ? (
          <div className="py-16 text-center" data-testid="admin-reviews-empty">
            <p className="font-serif-display text-2xl italic text-ink-soft">
              No pending reviews — readers' words will appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-6" data-testid="admin-reviews-list">
            {list.map((r, i) => (
              <li
                key={r.id}
                data-testid={`admin-review-row-${i}`}
                className="border border-rule p-6 bg-paper-2/40"
              >
                <p className="font-serif-display italic text-xl text-ink leading-snug">
                  "{r.quote}"
                </p>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-ink-soft">
                  <span className="text-ink">— {r.reviewer_name}</span>
                  {r.reviewer_title && (
                    <span className="ml-2 normal-case tracking-normal italic">
                      · {r.reviewer_title}
                    </span>
                  )}
                  {r.reviewer_email && (
                    <span className="ml-3 normal-case tracking-normal text-ink-soft/80">
                      {r.reviewer_email}
                    </span>
                  )}
                  <span className="ml-3 normal-case tracking-normal text-ink-soft/70">
                    · {new Date(r.created_at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button
                    onClick={() => approve(r.id)}
                    data-testid={`admin-review-approve-${i}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors"
                  >
                    <Check size={14} /> Approve & Publish
                  </button>
                  <button
                    onClick={() => reject(r.id)}
                    data-testid={`admin-review-reject-${i}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink-soft text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
                  >
                    <X size={14} /> Discard
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
